import React from 'react';
import { useCharitableGivingQuery } from './api/useCharitableGivingQuery';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Content, StickyFooter } from '../../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { FullPageLoading } from '../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { ErrorState } from '../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { CharitableGivingProvider } from './context/CharitableGivingProvider';
import { useCharitableGivingContext } from './context/useCharitableGivingContext';
import { GivingStrategy } from './steps/GivingStrategy/GivingStrategy';
import { KindOfProperty } from './steps/KindOfProperty/KindOfProperty';
import { Donor } from './steps/Donor/Donor';
import { DonationAmount } from './steps/DonationAmount/DonationAmount';
import { DonationYear } from './steps/DonationYear/DonationYear';
import { DafGrantYear } from './steps/DafGrantYear/DafGrantYear';
import { CharityName } from './steps/CharityName/CharityName';
import { FundAccounts } from './steps/FundAccounts/FundAccounts';
import { Review } from './steps/Review/Review';
import { DafAccount } from './steps/DafAccount/DafAccount';
import { DafGrantAmount } from './steps/DafGrantAmount/DafGrantAmount';
import { DafDonationYear } from './steps/DafDonationYear/DafDonationYear';

interface Props {
  onPreviousStage(): void;
}

export const CharitableGiving = ({ onPreviousStage }: Props) => {
  const { isLoading, isError, refetch, data } = useCharitableGivingQuery();

  if (isLoading) {
    return (
      <Box mt={96}>
        <Content>
          <FullPageLoading text="Loading..." size="medium" />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box mt={96}>
        <Content display="grid" css="place-items: center">
          <ErrorState onAction={refetch} />
        </Content>

        <StickyFooter />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <CharitableGivingProvider data={data} onPreviousStage={onPreviousStage}>
      <CharitableGivingContent />
    </CharitableGivingProvider>
  );
};

const CharitableGivingContent = () => {
  const { step } = useCharitableGivingContext();

  if (step === 'givingStrategy') {
    return <GivingStrategy />;
  }

  if (step === 'dafAccount') {
    return <DafAccount />;
  }

  if (step === 'kindOfProperty') {
    return <KindOfProperty />;
  }

  if (step === 'donor') {
    return <Donor />;
  }

  if (step === 'donationAmount') {
    return <DonationAmount />;
  }

  if (step === 'donationYear') {
    return <DonationYear />;
  }

  if (step === 'dafDonationYear') {
    return <DafDonationYear />;
  }

  if (step === 'dafGrantAmount') {
    return <DafGrantAmount />;
  }

  if (step === 'dafGrantYear') {
    return <DafGrantYear />;
  }

  if (step === 'charityName') {
    return <CharityName />;
  }

  if (step === 'fundAccounts') {
    return <FundAccounts />;
  }

  if (step === 'review') {
    return <Review />;
  }

  throw new Error(`Unknown step ${step}`);
};
